import api from './api';

/**
 * Goal Service
 * API client for goal-setting and progress tracking.
 */

export const goalService = {
  // ========== Goals CRUD ==========

  /**
   * Get list of goals with optional filters.
   * @param {Object} params - Query parameters
   * @param {string} [params.scope] - Filter by 'user' or 'firm'
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.period] - Filter by period
   * @param {number} [params.skip] - Pagination offset
   * @param {number} [params.limit] - Pagination limit
   */
  getGoals: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.scope) queryParams.append('scope', params.scope);
    if (params.status) queryParams.append('status', params.status);
    if (params.period) queryParams.append('period', params.period);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);

    const queryString = queryParams.toString();
    return api.get(`/goals/${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single goal by ID.
   * @param {string} goalId - Goal UUID
   * @param {boolean} [includeHistory] - Include progress snapshots
   */
  getGoal: (goalId, includeHistory = false) => {
    return api.get(`/goals/${goalId}?include_history=${includeHistory}`);
  },

  /**
   * Create a new goal.
   * @param {Object} goalData - Goal creation data
   */
  createGoal: (goalData) => {
    return api.post('/goals/', goalData);
  },

  /**
   * Update an existing goal.
   * @param {string} goalId - Goal UUID
   * @param {Object} goalData - Fields to update
   */
  updateGoal: (goalId, goalData) => {
    return api.put(`/goals/${goalId}`, goalData);
  },

  /**
   * Delete (cancel) a goal.
   * @param {string} goalId - Goal UUID
   */
  deleteGoal: (goalId) => {
    return api.delete(`/goals/${goalId}`);
  },

  // ========== Dashboard ==========

  /**
   * Get goals formatted for dashboard widget.
   * @param {string} viewType - 'my' for personal, 'firm' for firm-wide
   */
  getDashboardGoals: (viewType = 'my') => {
    return api.get(`/goals/dashboard?view_type=${viewType}`);
  },

  // ========== Metrics ==========

  /**
   * Get available metrics with metadata.
   * @param {string} [scope] - Filter by 'user' or 'firm'
   */
  getMetrics: (scope = null) => {
    const params = scope ? `?scope=${scope}` : '';
    return api.get(`/goals/metrics${params}`);
  },

  // ========== Settings ==========

  /**
   * Get current user's goal settings.
   */
  getUserSettings: () => {
    return api.get('/goals/settings');
  },

  /**
   * Update current user's goal settings.
   * @param {Object} settings - Settings to update
   */
  updateUserSettings: (settings) => {
    return api.put('/goals/settings', settings);
  },

  /**
   * Get firm-level goal settings (admin only).
   */
  getFirmSettings: () => {
    return api.get('/goals/firm/settings');
  },

  /**
   * Update firm-level goal settings (admin only).
   * @param {Object} settings - Settings to update
   */
  updateFirmSettings: (settings) => {
    return api.put('/goals/firm/settings', settings);
  },

  // ========== Progress Management ==========

  /**
   * Manually refresh a goal's progress.
   * @param {string} goalId - Goal UUID
   */
  refreshGoalProgress: (goalId) => {
    return api.post(`/goals/${goalId}/refresh`);
  },

  /**
   * Refresh all active goals (admin only).
   */
  refreshAllGoals: () => {
    return api.post('/goals/refresh-all/');
  },

  /**
   * Capture daily snapshots (admin only).
   */
  captureSnapshots: () => {
    return api.post('/goals/capture-snapshots/');
  },

  // ========== Helpers ==========

  /**
   * Get status color class for a goal status.
   * @param {string} status - Goal status
   */
  getStatusColor: (status) => {
    const colors = {
      active: 'bg-blue-100 text-blue-800',
      achieved: 'bg-green-100 text-green-800',
      missed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  /**
   * Get progress bar color based on progress and direction.
   * @param {number} progress - Progress percentage
   * @param {string} direction - Goal direction
   * @param {boolean} isOnTrack - Whether goal is on track
   */
  getProgressColor: (progress, direction, isOnTrack) => {
    if (progress >= 100) return 'bg-green-500';
    if (!isOnTrack) return 'bg-yellow-500';
    if (progress >= 75) return 'bg-green-400';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-400';
    return 'bg-red-400';
  },

  /**
   * Format a metric value for display.
   * @param {number} value - The value
   * @param {string} metricType - The metric type
   */
  formatMetricValue: (value, metricType) => {
    const percentMetrics = ['current_utilization', 'court_deadlines_met', 'case_progress_percentage', 'team_utilization'];
    const hoursMetrics = ['billable_hours_month'];

    if (percentMetrics.includes(metricType)) {
      return `${Number(value).toFixed(1)}%`;
    }
    if (hoursMetrics.includes(metricType)) {
      return `${Number(value).toFixed(1)}h`;
    }
    return Number(value).toFixed(0);
  },

  /**
   * Get human-readable period label.
   * @param {string} period - Period type
   */
  getPeriodLabel: (period) => {
    const labels = {
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    };
    return labels[period] || period;
  },

  /**
   * Get direction icon.
   * @param {string} direction - Goal direction
   */
  getDirectionIcon: (direction) => {
    switch (direction) {
      case 'maximize':
        return '↑';
      case 'minimize':
        return '↓';
      case 'target':
        return '◎';
      default:
        return '';
    }
  },

  /**
   * Calculate period dates based on period type.
   * @param {string} period - Period type
   * @returns {Object} { start: Date, end: Date }
   */
  calculatePeriodDates: (period) => {
    const today = new Date();
    let start, end;

    switch (period) {
      case 'weekly':
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start = new Date(today);
        start.setDate(today.getDate() + mondayOffset);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;

      case 'monthly':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;

      case 'quarterly':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        break;

      case 'yearly':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;

      default:
        start = today;
        end = new Date(today);
        end.setDate(today.getDate() + 30);
    }

    return { start, end };
  },

  /**
   * Format date for API (YYYY-MM-DD).
   * @param {Date} date - Date object
   */
  formatDateForApi: (date) => {
    return date.toISOString().split('T')[0];
  },
};

export default goalService;
